import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { useFmt } from "@/components/v52/Bits";
import { useT52 } from "@/i18n/v52";

export const REASONS = ["closed", "duplicate", "not_found", "not_brocki", "wrong_address", "other"];
const CHIP = { confirmed: "ok", rejected: "fail", pending: "unk" };

function Tile({ label, value, testid, warn }) {
  return <div className="card-surface rounded-xl" data-testid={testid}><small>{label}</small><strong className={warn ? "f52-imp-warn" : ""}>{value ?? "—"}</strong></div>;
}

export function VerTiles({ s }) {
  const { t52 } = useT52();
  const m = s.by_method || {};
  return (
    <div className="f52-ops-tiles-wrap">
      <div className="f52-ops-tiles f52-ver-tiles">
        <Tile label={t52("ver_c_confirmed")} value={s.confirmed} testid="ver-count-confirmed" />
        <Tile label={t52("ver_c_target")} value={s.target} testid="ver-count-target" />
        <Tile label={t52("ver_c_gap")} value={s.gap} testid="ver-count-gap" warn={s.gap > 0} />
        <Tile label={t52("ver_c_pending")} value={s.pending} testid="ver-count-pending" />
        <Tile label={t52("ver_c_rejected")} value={s.rejected} testid="ver-count-rejected" />
      </div>
      <p className="f52-imp-muted" data-testid="ver-count-methods">{t52("ver_c_methods", { f: m.source_file || 0, a: m.auto_official || 0, m: m.manual || 0 })}</p>
      {s.discovered?.pending_review > 0 && <p className="f52-imp-muted" data-testid="ver-count-discovered">{t52("ver_c_discovered", { n: s.discovered.pending_review })}</p>}
    </div>
  );
}

function Link({ label, url, testid }) {
  if (!url) return null;
  return <a href={url} target="_blank" rel="noopener noreferrer" className="f52-ver-link" data-testid={testid}>{label}<ExternalLink aria-hidden="true" /></a>;
}

function Evidence({ r }) {
  const { t52 } = useT52();
  const a = r.auto;
  if (!a) return null;
  return (
    <p className="f52-imp-muted f52-ver-links" data-testid={`ver-row-${r.row_id}-auto`}>
      <span>{t52(`ver_auto_${a.result}`)}</span>
      {a.evidence.map((e, i) => <Link key={`${i}-${e.url}`} label={`${t52("ver_l_evidence")} ${i + 1}`} url={e.url} testid={`ver-row-${r.row_id}-ev-${i}`} />)}
    </p>
  );
}

export function VerRow({ r, onDecide }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const id = r.row_id;
  return (
    <li data-testid={`ver-row-${id}`}>
      <div>
        <h3>{r.name} <span className="f52-mono f52-imp-muted">{id}</span></h3>
        <p>{[r.street, `${r.plz || ""} ${r.city || ""}`.trim(), r.canton].filter(Boolean).join(" · ")}{r.operator ? ` · ${r.operator}` : ""}</p>
        <p className="f52-ver-links">
          <Link label={t52("ver_l_official")} url={r.official} testid={`ver-row-${id}-official`} />
          {r.website !== r.official && <Link label={t52("ver_l_web")} url={r.website} testid={`ver-row-${id}-web`} />}
          <Link label={t52("ver_l_second")} url={r.second} testid={`ver-row-${id}-second`} />
          {r.status === "confirmed" && <Link label={t52("ver_l_proof")} url={r.source_url} testid={`ver-row-${id}-proof`} />}
        </p>
        {r.status === "pending" ? <Evidence r={r} /> : (
          <p className="f52-imp-muted" data-testid={`ver-row-${id}-decision`}>
            {[r.method && t52(`ver_m_${r.method}`), r.reason ? t52(`ver_r_${r.reason}`) : r.note,
              r.decided_by && t52("ver_by", { who: r.decided_by, t: fmt(r.decided_at) })].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
      <div className="acts">
        <span className={`f52-chip ${CHIP[r.status]}`} data-testid={`ver-row-${id}-status`}>{t52(`ver_st_${r.status}`)}</span>
        {r.status !== "confirmed" && !["DUPLICATE", "INVALID", "CLOSED"].includes(r.classification) && !["no_address", "duplicate_address"].includes(r.reason) && (
          <button type="button" className="f52-btn f52-btn-secondary" onClick={() => onDecide(r, "confirm")} data-testid={`ver-row-${id}-confirm`}>{t52("ver_confirm")}</button>
        )}
        {r.status !== "rejected" && <button type="button" className="f52-btn f52-btn-secondary" onClick={() => onDecide(r, "reject")} data-testid={`ver-row-${id}-reject`}>{t52("ver_reject")}</button>}
      </div>
    </li>
  );
}

export function VerDialog({ dlg, onCancel, onSubmit }) {
  const { t52 } = useT52();
  const ref = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const confirm = dlg.decision === "confirm";
  const [v, setV] = useState({ source_url: confirm ? (dlg.row.official || dlg.row.website || "") : "", reason: "closed", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.querySelector("input, select")?.focus();
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); cancelRef.current(); } };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => { e.preventDefault(); setBusy(true); await onSubmit(v); setBusy(false); };
  return (
    <div ref={ref} className="f52-dlg" role="dialog" aria-modal="true" aria-labelledby="ver-dlg-t" data-testid="ver-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <form className="box" onSubmit={submit}>
        <h2 id="ver-dlg-t">{t52(confirm ? "ver_d_confirm" : "ver_d_reject")}</h2>
        <p><b>{dlg.row.name}</b> · {dlg.row.street}, {dlg.row.plz} {dlg.row.city} <span className="f52-mono">{dlg.row.row_id}</span></p>
        {confirm ? (
          <label className="f52-imp-field">{t52("ver_d_url")}
            <input type="url" value={v.source_url} onChange={set("source_url")} required data-testid="ver-dialog-url" />
          </label>
        ) : (
          <label className="f52-imp-field">{t52("ver_d_reason")}
            <select value={v.reason} onChange={set("reason")} data-testid="ver-dialog-reason">
              {REASONS.map((x) => <option key={x} value={x}>{t52(`ver_r_${x}`)}</option>)}
            </select>
          </label>
        )}
        <label className="f52-imp-field">{t52("ver_d_note")}
          <textarea value={v.note} onChange={set("note")} maxLength={500} data-testid="ver-dialog-note" />
        </label>
        <div className="acts">
          <button type="button" className="f52-btn f52-btn-secondary" onClick={onCancel} data-testid="ver-dialog-cancel">{t52("ver_cancel")}</button>
          <button type="submit" className="f52-btn f52-btn-primary" disabled={busy} data-testid="ver-dialog-submit">{t52("ver_save")}</button>
        </div>
      </form>
    </div>
  );
}
